import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Edit2, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import DatePicker from '../DatePicker';
import RowActionsMenu from '../RowActionsMenu';
import TablePager, { usePagedRows } from '../TablePager';
import { clinicToday, formatClinicDate } from '../../utils/clinicTime';
import { datePart } from './attendanceStatus';

// Hospital holidays, one list per year. A holiday is not counted as absent, is not a leave day when
// it falls inside a leave range, and is paid. Nothing is seeded: Eid moves every year, so the
// hospital enters its own dates.

const yearOf = (date) => Number(String(date).slice(0, 4));

const HolidayModal = ({ existing, onClose, onSaved }) => {
  const notify = useToast();
  const [date, setDate] = useState(existing?.date || clinicToday());
  const [name, setName] = useState(existing?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialog(() => { if (!saving) onClose(); });

  const submit = async () => {
    if (!date) { setError('Pick the date.'); return; }
    if (!name.trim()) { setError('Name the holiday.'); return; }
    setError('');
    setSaving(true);
    try {
      const saved = existing
        ? await attendanceAPI.updateHoliday(existing.id, { date, name: name.trim() })
        : await attendanceAPI.createHoliday({ date, name: name.trim() });
      notify('success', existing ? 'Holiday updated' : 'Holiday added');
      onSaved(saved);
    } catch (e) {
      setError(e?.code === 'HOLIDAY_EXISTS' ? 'That date is already a holiday.' : e?.message || 'The holiday could not be saved.');
      setSaving(false);
    }
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label={existing ? 'Edit holiday' : 'Add holiday'}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              {existing ? <Edit2 className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
            </div>
            <h2 className="text-lg font-bold text-white">{existing ? 'Edit holiday' : 'Add holiday'}</h2>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={saving} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Date <span className="text-red-500">*</span></label>
            <DatePicker value={date} onChange={setDate} disabled={saving} />
          </div>
          <div>
            <label htmlFor="holiday-name" className="block text-sm font-semibold mb-1">Name <span className="text-red-500">*</span></label>
            <input
              id="holiday-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={150}
              disabled={saving}
              autoFocus
              placeholder="e.g. Eid ul Fitr"
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : existing ? 'Save holiday' : 'Add holiday'}
          </button>
        </div>
      </div>
    </div>
  );
};

const HolidaysSettings = ({ canEdit }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const [year, setYear] = useState(yearOf(clinicToday()));
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // a holiday, or 'new'

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await attendanceAPI.holidays(year);
      setRows(Array.isArray(res) ? res : []);
      setError(null);
    } catch (e) {
      setError(e?.message || 'The holidays could not be loaded.');
    }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const remove = async (holiday) => {
    const ok = await confirm({
      title: `Remove "${holiday.name}"?`,
      message: `${formatClinicDate(holiday.date)} goes back to being an ordinary working day.`,
      confirmText: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await attendanceAPI.deleteHoliday(holiday.id);
      notify('success', 'Holiday removed');
      load();
    } catch (e) {
      notify('error', e?.message || 'The holiday could not be removed.');
    }
  };

  const thisYear = yearOf(clinicToday());
  const paging = usePagedRows(rows, { id: 'holidays', resetOn: [year] });

  return (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Holidays</h2>
          <p className="text-sm text-gray-600">Days the hospital is closed. They are paid, and never counted as absent or as leave.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1" role="group" aria-label="Year">
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
              <button
                key={y}
                type="button"
                aria-pressed={year === y}
                onClick={() => setYear(y)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${year === y ? 'bg-navy-800 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
              >
                {y}
              </button>
            ))}
          </div>
          {canEdit && (
            <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
              <Plus className="w-4 h-4" aria-hidden="true" /> Add holiday
            </button>
          )}
        </div>
      </div>

      {error ? (
        <p className="p-5 text-sm text-red-700">{error}</p>
      ) : !rows ? (
        <p className="p-10 text-center text-sm text-gray-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" aria-hidden="true" />Loading…</p>
      ) : rows.length === 0 ? (
        <p className="p-10 text-center text-sm text-gray-500">
          <CalendarDays className="w-6 h-6 text-gray-300 mx-auto mb-2" aria-hidden="true" />
          No holidays entered for {year}.
        </p>
      ) : (
        <>
        {/* Square corners: the bar sits under the card's heading, not at its top edge. */}
        <TablePager paging={paging} noun="holiday" className="px-5" rounded={false} />
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <th className="px-5 py-3">Date</th>
              <th className="px-4 py-3">Day</th>
              <th className="px-4 py-3">Holiday</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paging.pageRows.map((h) => (
              <tr key={h.id}>
                <td className="px-5 py-3 text-gray-900 font-medium">{formatClinicDate(h.date)}</td>
                <td className="px-4 py-3 text-gray-600">{datePart(h.date, { weekday: 'long' })}</td>
                <td className="px-4 py-3 text-gray-700">{h.name}</td>
                <td className="px-4 py-3 text-right">
                  {canEdit ? (
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', icon: Edit2, iconClass: 'text-emerald-500', onClick: () => setEditing(h) },
                        { key: 'remove', label: 'Remove', icon: Trash2, danger: true, onClick: () => remove(h) },
                      ]}
                    />
                  ) : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager paging={paging} noun="holiday" position="bottom" className="px-5" />
        </>
      )}

      {editing && (
        <HolidayModal
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => { setEditing(null); setYear(yearOf(saved?.date || `${year}-01-01`)); load(); }}
        />
      )}
    </div>
  );
};

export default HolidaysSettings;
